import {
  CategoryHashes,
  CategoryHits,
  CategoryLookup,
  CategoryModelFolder,
  CategoryModelVersionApplication,
  Expression,
} from "./category-builder.models";
import { CategoryModelService } from "./category-model.service";

function getCategoryLookup(
  category_tree: CategoryModelFolder[],
): CategoryLookup {
  return category_tree?.reduce(
    (dict: CategoryLookup, folder: CategoryModelFolder) => {
      const { parentFolder } =
        CategoryModelService.createParentFolderWithCatRefChildren(folder);

      folder.children.forEach((category) => {
        // Add unaltered L2 to dict
        dict[category.id] = category;
      });

      // Add the L1 to the dict as well in order to get sentence results for all of it's children combined
      dict[parentFolder.id] = parentFolder;

      return dict;
    },
    {},
  );
}

function getCategoryList(category_tree: CategoryModelFolder[]): Expression[] {
  return category_tree?.reduce(
    (list: Expression[], folder: CategoryModelFolder) => {
      const { parentFolder } =
        CategoryModelService.createParentFolderWithCatRefChildren(folder);
      return [...list, parentFolder, ...folder.children];
    },
    [],
  );
}

function categoryMetrics(
  results: CategoryModelVersionApplication,
  hashes: CategoryHashes,
  categories: Expression[],
  lookup: CategoryLookup,
): { [categoryID: string]: CategoryHits } {
  // We need to produce a map of each category id to its
  //    - Total number of category hits
  //    - List of co-occurrences with all other categories
  //    - Whether or not the numbers for the category were produced with a hash
  //      differing from the category's current hash. We call this "dirty".
  return (
    categories.reduce(
      (
        categoryHits: { [categoryID: string]: CategoryHits },
        category: Expression,
      ) => {
        // The hash of the category at the time the numbers were produced
        const appliedHash = results.application_data[category.id];

        // Co occurrences for only the category in question
        const cos = results.co_occurrences.filter(
          (co) =>
            co.expressionA === category.id || co.expressionB === category.id,
        );

        return {
          ...categoryHits,
          [category.id]: {
            // Simply look up the total sentence and verbatim hits
            totalSentences: results.category_sentence_hits[category.id],
            totalVerbatims: results.category_verbatim_hits[category.id],

            // List of co-occurrences with other categories
            coOccurrences: cos
              .map((co) => {
                const otherCategoryID =
                  category.id === co.expressionA
                    ? co.expressionB
                    : co.expressionA;
                return {
                  category: lookup[otherCategoryID],
                  coOccurrences: co.count,
                };
              })
              // We only care about categories with which there are co-occurrences
              .filter((co) => co.coOccurrences > 0)
              // Only if category existed in lookup
              .filter((co) => co.category)
              // Sort by descending co-occurrence numbers
              .sort((a, b) => {
                if (a.coOccurrences > b.coOccurrences) {
                  return -1;
                } else if (a.coOccurrences < b.coOccurrences) {
                  return 1;
                }
                return 0;
              }),
            // Mark dirty if the current hash doesn't match the hash when it was applied
            dirty: hashes[category.id] !== appliedHash,
          },
        };
      },
      {},
    ) || {}
  );
}

export const CategoryModelUtils = {
  getCategoryLookup,
  getCategoryList,
  categoryMetrics,
};
